import React, { useEffect, useRef, useState } from 'react';
// import { useTranslation } from 'react-i18next';
import { Button as MIButton } from '@material-ui/core';
import { connect } from 'react-redux';
import Tooltip from '@material-ui/core/Tooltip';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { CreatePayoutModal } from './components/create-payouts/create-payout-modal.component';
import { BulkPayoutModal } from './components/bulk-payouts/bulk-payout-modal.component';
import moment from 'moment';
import { CreatePayOutDto, Payout } from '../../../shared/models/payout.model';
import { UnitTypeUiModel } from '../../../shared/ui-models/unit-type.ui-model';
import { createPayouts, deletePayout } from '../../shared/services/data.service';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { getPayouts } from '../../../ExpensesModule/shared/services/data.service';
import { RootState } from '../../../shared/redux/reducers';
import { ExpensesPayoutListFilterContainer } from '../../../shared/components/widgets/expenses-payout-list-filter/expenses-payout-list-filter.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { ConfirmModal } from '../../../shared/components/core-ui/confirm-modal/confirm-modal.component';

interface PayoutListOwnProps {
  unitType?: UnitTypeUiModel;
  addPayoutHandler: (unitPayout: CreatePayOutDto) => void;
  deletePayoutHandler: (unitPayout: Payout) => void;
}
type PayoutDraft = CreatePayOutDto & { file?: File };
export type PayoutsListCoContainerProps = ReturnType<typeof mapStateToProps>;

const payoutAmountFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2
});

/**
 * functional component ReservationsListContainer
 * @param {PayoutsListCoContainerProps} holding question text
 */
export const PayoutsListContainer: React.FC<PayoutsListCoContainerProps> = ({ unitType }) => {
  const [showCreateReseration, setShowCreateReseration] = useState<boolean>(false);
  const [showBulkPayout, setShowBulkPayout] = useState<boolean>(false);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [payoutToDelete, setPayoutToDelete] = useState<Payout | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const latestRequest = useRef(0);

  useEffect(() => {
    getPayoutList({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPayoutList = (query: object, notifySuccess = true) => {
    const requestId = ++latestRequest.current;
    setIsLoading(true);
    const promise = getPayouts(query)
      .then((response) => {
        if (isErrorInfo(response)) {
          throw response;
        }
        if (requestId === latestRequest.current) {
          setPayouts(response);
        }
        return response;
      })
      .finally(() => {
        if (requestId === latestRequest.current) {
          setIsLoading(false);
        }
      });

    if (!notifySuccess) {
      promise.catch(() => birdToast.error('Failed to load payouts', 'Payout'));
      return promise;
    }

    birdToast.promise(promise, {
      title: 'Payout',
      loadingMessage: 'Loading payouts...',
      successMessage: 'Payouts loaded successfully',
      errorFailedMessage: 'Failed to load payouts',
      errorPermissionMessage: 'You do not have permission to view payouts'
    });

    return promise;
  };

  const addPayoutHandler = (payout: PayoutDraft) => {
    const promise = createPayouts(payout, payout.file as File).then((response) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      getPayoutList({ unitTypeId: Number(unitType?.id) || 0 }, false);
      return response;
    });

    birdToast.promise(promise, {
      title: 'Payout',
      loadingMessage: 'Creating payout...',
      successMessage: 'Payout created successfully',
      errorFailedMessage: 'Failed to create payout',
      errorPermissionMessage: 'You do not have permission to create payouts'
    });
  };
  const deletePayoutHandler = (payout: Payout) => {
    const promise = deletePayout(payout.id).then((response) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      getPayoutList({ unitTypeId: Number(unitType?.id) || 0 }, false);
      return response;
    });

    birdToast.promise(promise, {
      title: 'Payout',
      loadingMessage: 'Deleting payout...',
      successMessage: 'Payout deleted successfully',
      errorFailedMessage: 'Failed to delete payout',
      errorPermissionMessage: 'You do not have permission to delete payouts'
    });
  };

  const searchPayouts = (filteredData: {
    room: string;
    description: string;
    project: string;
    area: string;
    category: string;
    paymentDateFrom: Date;
    paymentDateTo: Date;
    amount: string;
  }) => {
    getPayoutList(filteredData, false);
  };
  const totalPaid = payouts.reduce((sum, payout) => sum + (Number(payout.amount) || 0), 0);

  function Row(props: { payout: Payout }) {
    const { payout } = props;
    return (
      <TableRow className="transition-colors hover:!bg-[#f7faf8]">
        <TableCell className="!max-w-[23rem] !border-[#e8edf1] !px-6 !py-5 !text-[1.25rem] !font-bold !leading-snug !text-[#1e2a35]">
          {payout?.description || '—'}
        </TableCell>
        <TableCell>{payout?.roomName || '—'}</TableCell>
        <TableCell>{payout?.projectName || '—'}</TableCell>
        <TableCell>{payout?.areaName || '—'}</TableCell>
        <TableCell className="!whitespace-nowrap !tabular-nums">
          {moment(payout.paymentDate).format('MMM D, YYYY')}
        </TableCell>
        <TableCell>
          {payout?.category ? (
            <span className="inline-flex max-w-[16rem] overflow-hidden rounded-full bg-[#eaf3ed] px-3 py-1 text-[1.05rem] font-bold leading-tight text-[#2e6745] text-ellipsis whitespace-nowrap">
              {payout.category}
            </span>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell className="!text-right !text-[1.35rem] !font-extrabold !tabular-nums !text-[#183f2a]">
          {payoutAmountFormatter.format(payout.amount)}
        </TableCell>
        <TableCell>{payout.updateByUser || '—'}</TableCell>
        <TableCell className="!whitespace-nowrap !tabular-nums">
          {moment(payout.updatedAt).format('MMM D, YYYY')}
        </TableCell>

        <TableCell className="!min-w-[11rem] !text-right">
          <div className="flex justify-end gap-2">
            <Tooltip title="Delete payout" arrow>
              <MIButton
                aria-label="Delete payout"
                className="!h-10 !min-w-[4rem] !rounded-lg !p-0 !text-[#52616f] transition-[background-color,color,transform] hover:!translate-y-[-1px] hover:!bg-[#fcebed] hover:!text-[#b33747] active:!scale-[0.96] focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-[#b33747]"
                onClick={() => {
                  setPayoutToDelete(payout);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width={24}
                  height={24}
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M19.5 5.5L18.8803 15.5251C18.7219 18.0864 18.6428 19.3671 18.0008 20.2879C17.6833 20.7431 17.2747 21.1273 16.8007 21.416C15.8421 22 14.559 22 11.9927 22C9.42312 22 8.1383 22 7.17905 21.4149C6.7048 21.1257 6.296 20.7408 5.97868 20.2848C5.33688 19.3626 5.25945 18.0801 5.10461 15.5152L4.5 5.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M9.5 16.5L9.5 10.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M14.5 16.5L14.5 10.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </MIButton>
            </Tooltip>
            {payout.paymentReceipt?.receiptUrl && (
              <Tooltip title="Open receipt in a new tab" arrow>
                <MIButton
                  aria-label="Open payout receipt"
                  className="!h-10 !min-w-[4rem] !rounded-lg !p-0 !text-[#52616f] transition-[background-color,color,transform] hover:!translate-y-[-1px] hover:!bg-[#e8f0eb] hover:!text-[#24603d] active:!scale-[0.96] focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-[#287148]"
                  component="a"
                  href={payout.paymentReceipt.receiptUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width={24}
                    height={24}
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M4 12.0004L4 14.5446C4 17.7896 4 19.4121 4.88607 20.5111C5.06508 20.7331 5.26731 20.9354 5.48933 21.1144C6.58831 22.0004 8.21082 22.0004 11.4558 22.0004C12.1614 22.0004 12.5141 22.0004 12.8372 21.8864C12.9044 21.8627 12.9702 21.8354 13.0345 21.8047C13.3436 21.6569 13.593 21.4074 14.0919 20.9085L18.8284 16.172C19.4065 15.5939 19.6955 15.3049 19.8478 14.9374C20 14.5698 20 14.1611 20 13.3436V10.0004C20 6.22919 20 4.34358 18.8284 3.172C17.7693 2.11284 16.1265 2.01122 13.0345 2.00146M13 21.5004V21.0004C13 18.172 13 16.7578 13.8787 15.8791C14.7574 15.0004 16.1716 15.0004 19 15.0004H19.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 8.23028V5.46105C4 3.54929 5.567 1.99951 7.5 1.99951C9.433 1.99951 11 3.54929 11 5.46105V9.26874C11 10.2246 10.2165 10.9995 9.25 10.9995C8.2835 10.9995 7.5 10.2246 7.5 9.26874V5.46105"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </MIButton>
              </Tooltip>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  }
  return (
    <div className="w-full min-h-[calc(100vh-45px)] space-y-3.5 bg-[#f8f9fa] px-4 py-6 text-slate-800 antialiased sm:px-6 lg:px-8">
      <CreatePayoutModal
        config={{
          showModal: showCreateReseration,
          unitType
        }}
        eventHandlers={{
          closeHandler: () => {
            setShowCreateReseration(false);
          },
          addPayoutHandler: (payout) => {
            setShowCreateReseration(false);
            addPayoutHandler(payout);
          }
        }}
      ></CreatePayoutModal>
      <ConfirmModal
        config={{
          showModal: !!payoutToDelete,
          message: payoutToDelete
            ? `Delete the payout "${payoutToDelete.description || '—'}"${
                payoutToDelete.roomName ? ` for ${payoutToDelete.roomName}` : ''
              } of ${payoutAmountFormatter.format(payoutToDelete.amount)}? This can't be undone.`
            : ''
        }}
        eventHandlers={{
          denyAction: () => setPayoutToDelete(null),
          confirmAction: () => {
            if (payoutToDelete) {
              deletePayoutHandler(payoutToDelete);
            }
            setPayoutToDelete(null);
          }
        }}
      />
      <BulkPayoutModal
        showModal={showBulkPayout}
        closeHandler={() => setShowBulkPayout(false)}
        createdHandler={(result) => {
          setShowBulkPayout(false);
          if (result.skipped.length) {
            birdToast.warning(
              `${result.skipped.length} rows were skipped: ${result.skipped
                .map((row) => row.reference || row.index + 1)
                .join(', ')}`,
              'Bulk payout'
            );
          }
          getPayoutList({}, false);
        }}
      />
      <section className="flex flex-col justify-between gap-3 rounded border border-slate-200/80 bg-white px-5 py-4 shadow-sm md:flex-row md:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Payouts</h1>
            <span className="inline-flex items-center rounded-full border border-primary-200/60 bg-primary-50 px-2.5 py-0.5 text-sm font-semibold text-primary-700">
              {isLoading ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
                  Updating…
                </span>
              ) : (
                `${payouts.length} ${payouts.length === 1 ? 'record' : 'records'}`
              )}
            </span>
          </div>
          <div className="hidden h-4 w-px bg-slate-200 sm:block" />
          <p className="hidden text-sm text-slate-500 lg:inline">
            Monitor and review property transactions
          </p>
          <div className="hidden h-4 w-px bg-slate-200 sm:block" />
          <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-slate-50 px-3 py-1.5 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Total paid:
            </span>
            <span className="font-bold tabular-nums text-slate-900">
              {totalPaid.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 self-end md:self-auto">
          <button
            type="button"
            onClick={() => setShowBulkPayout(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 active:scale-[0.98]"
          >
            Bulk Payouts
          </button>
          <button
            type="button"
            onClick={() => setShowCreateReseration(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-5 py-2.5 text-base font-semibold text-white shadow-sm shadow-primary-500/25 transition-all hover:bg-primary-600 active:scale-[0.98]"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path d="M12 4.5v15m7.5-7.5h-15" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Create Payouts
          </button>
        </div>
      </section>

      <ExpensesPayoutListFilterContainer
        data={payouts}
        entityLabel="payouts"
        eventHandlers={{
          search: (data: {
            room: string;
            description: string;
            project: string;
            area: string;
            category: string;
            paymentDateFrom: Date;
            paymentDateTo: Date;
            amount: string;
          }) => searchPayouts(data),
          getInitialList: () => getPayoutList({}, false)
        }}
      />

      <div
        aria-busy={isLoading}
        className={`relative transition-opacity duration-200 ${isLoading ? 'opacity-60' : ''}`}
      >
        {isLoading && (
          <div className="absolute inset-x-0 top-0 z-10 h-1 overflow-hidden rounded-t bg-primary-100">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-primary-500" />
          </div>
        )}
      <TableContainer
        component={Paper}
        className="overflow-x-auto rounded-2xl border border-[#e1e7ed] bg-[#fbfcfe] shadow-[0_1px_2px_rgba(28,41,53,0.04),0_12px_30px_rgba(28,41,53,0.06)]"
      >
        <Table aria-label="Payout records" className="min-w-[112rem]">
          <TableHead className="[&_.MuiTableCell-root]:!border-b [&_.MuiTableCell-root]:!border-[#dbe3ea] [&_.MuiTableCell-root]:!bg-[#f1f4f7] [&_.MuiTableCell-root]:!px-6 [&_.MuiTableCell-root]:!py-4 [&_.MuiTableCell-root]:!text-[1.05rem] [&_.MuiTableCell-root]:!font-extrabold [&_.MuiTableCell-root]:!tracking-[0.055em] [&_.MuiTableCell-root]:!text-[#64717d] [&_.MuiTableCell-root]:uppercase [&_.MuiTableCell-root]:whitespace-nowrap">
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell>Room</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Area</TableCell>
              <TableCell>Payment date</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Last updated by</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="[&_.MuiTableCell-root]:!border-[#e8edf1] [&_.MuiTableCell-root]:!px-6 [&_.MuiTableCell-root]:!py-5 [&_.MuiTableCell-root]:!text-[1.25rem] [&_.MuiTableCell-root]:!text-[#344250]">
            {payouts?.map((payout) => (
              <Row key={payout.id} payout={payout} />
            ))}
            {!payouts.length && (
              <TableRow>
                <TableCell colSpan={10} className="!h-[20rem] !text-center">
                  <strong className="mb-2 block text-[1.55rem] text-[#2d3c49]">
                    No payouts found
                  </strong>
                  <span className="block text-[1.25rem] text-[#63717f]">
                    Try changing the filters or create a new payout.
                  </span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(PayoutsListContainer);

/**
 * function to to map redux state to component props
 * @param state root state or redux
 */
function mapStateToProps(state: RootState, props: PayoutListOwnProps) {
  return {
    isAuthenticated: !!state.auth.accessToken,
    ...props
  };
}
